import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Spinner } from '@components/ui';
import { clientService } from '@services';

export default function ClientPortalLogin({ token, onSuccess, onError }) {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [loadingClientInfo, setLoadingClientInfo] = useState(true);
  const inputRefs = useRef([]);

  // Fetch client info on mount to show logo
  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const response = await clientService.getPortalData(token);
        if (response.success) {
          setClientInfo(response.data);
        }
      } catch (err) {
        console.error('Error fetching client info:', err);
      } finally {
        setLoadingClientInfo(false);
      }
    };
    fetchClientInfo();
  }, [token]);

  // Focus first input on mount
  useEffect(() => {
    if (!loadingClientInfo) {
      inputRefs.current[0]?.focus();
    }
  }, [loadingClientInfo]);

  const handlePinChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        handleSubmit(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newPin = [...pin];
      for (let i = 0; i < pastedData.length; i++) {
        newPin[i] = pastedData[i];
      }
      setPin(newPin);
      if (pastedData.length === 6) {
        handleSubmit(pastedData);
      } else {
        inputRefs.current[pastedData.length]?.focus();
      }
    }
  };

  const handleSubmit = async (pinValue) => {
    const fullPin = pinValue || pin.join('');
    if (fullPin.length !== 6) {
      setError('Ingresa el PIN de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await clientService.validatePortalAccess(token, fullPin);
      if (response.success) {
        onSuccess(response.data);
      } else {
        setError(response.message || 'PIN incorrecto');
        setPin(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('PIN incorrecto');
      } else {
        setError('Error de conexión. Intenta de nuevo.');
      }
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingClientInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {clientInfo?.logo ? (
            <img
              src={clientInfo.logo}
              alt={clientInfo.name || 'Logo'}
              className="w-16 h-16 rounded-2xl mx-auto mb-4 object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-700 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">
              🚀
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{clientInfo?.name || 'JIROX'}</h1>
          <p className="text-slate-400 mt-1">Portal del Cliente</p>
        </div>

        <Card>
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              🔐
            </div>
            <h2 className="text-xl font-bold text-slate-800">Ingresa tu PIN</h2>
            <p className="text-slate-500 text-sm mt-2">
              Introduce el PIN de 6 dígitos que te proporcionaron
            </p>
          </div>

          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                className={`
                  w-12 h-14 text-center text-2xl font-bold rounded-xl border-2
                  transition-all duration-200 outline-none
                  ${error
                    ? 'border-red-300 bg-red-50'
                    : digit
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 bg-white'
                  }
                  focus:border-primary-500 focus:ring-2 focus:ring-primary-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              />
            ))}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center mb-4">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center mb-4">
              <Spinner size="md" />
            </div>
          )}

          <Button
            onClick={() => handleSubmit()}
            variant="primary"
            className="w-full"
            disabled={pin.join('').length !== 6 || isLoading}
          >
            {isLoading ? 'Verificando...' : 'Acceder al Portal'}
          </Button>

          <p className="text-xs text-slate-400 text-center mt-4">
            Si no tienes el PIN, contacta con tu gestor de proyecto
          </p>
        </Card>
      </div>
    </div>
  );
}

ClientPortalLogin.propTypes = {
  token: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
};
